import { Box, Typography } from "@mui/material";
import { addDays, differenceInMinutes, endOfWeek, max, min, parseISO,startOfDay, startOfWeek } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SCHEDULER_INFO_BAR_HEIGHT } from "../../filters/constants";
import { Shift } from "../store/types";

interface SchedulerInfoBarProps {
  shifts: Shift[];
  isWeekView: boolean;
  currentDate: string | Date;
  weekStartDay: string;
  isLoading?: boolean;
}

const getDayNumber = (dayName: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 => {
  const days: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  return days[dayName?.toLowerCase()] ?? 1;
};

const SchedulerInfoBar: React.FC<SchedulerInfoBarProps> = ({
  shifts,
  isWeekView,
  currentDate,
  weekStartDay,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { totalPublishedHours, totalUnpublishedHours, totalAcceptedHours } = useMemo(() => {
    if (!shifts || !currentDate || isLoading) {
      return { totalPublishedHours: t("scheduler_zero-hours"), totalUnpublishedHours: t("scheduler_zero-hours"), totalAcceptedHours: t("scheduler_zero-hours") };
    }

    const date = typeof currentDate === "string" ? parseISO(currentDate) : currentDate;
    const weekStartsOn = getDayNumber(weekStartDay);

    const viewStart = isWeekView
      ? startOfWeek(date, { weekStartsOn })
      : startOfDay(date);
    const viewEnd = isWeekView
      ? startOfDay(addDays(endOfWeek(date, { weekStartsOn }), 1))
      : startOfDay(addDays(date, 1));

    let publishedHours = 0;
    let unpublishedHours = 0;
    let acceptedHours = 0;

    shifts.forEach((shift: Shift) => {
      const shiftStart = parseISO(shift.startDate || shift.startDateTime);
      const shiftEnd = parseISO(shift.endDate || shift.endDateTime);
      const shiftDurationMinutes = differenceInMinutes(shiftEnd, shiftStart);
      const shiftMidpoint = new Date(shiftStart.getTime() + (shiftDurationMinutes / 2) * 60 * 1000);

      const overlapStart = max([shiftStart, viewStart]);
      const overlapEnd = min([shiftEnd, viewEnd]);

      const overlapMinutes = Math.max(0, differenceInMinutes(overlapEnd, overlapStart));
      const overlapHours = overlapMinutes / 60;

      if (shift.isPublished) {
        publishedHours += (shift.numberOfWorkersNeeded || 0) * overlapHours;
      } else {
        unpublishedHours += (shift.numberOfWorkersNeeded || 0) * overlapHours;
      }

      if (shift.fullSlots !== undefined || shift.firstHalfSlots !== undefined || shift.secondHalfSlots !== undefined) {
        // More precise calculation using partial slots
        const fullSlots = shift.fullSlots || 0;
        const firstHalfSlots = shift.firstHalfSlots || 0;
        const secondHalfSlots = shift.secondHalfSlots || 0;

        // Full slots
        acceptedHours += fullSlots * overlapHours;

        // First half slots
        const firstHalfOverlapStart = max([shiftStart, viewStart]);
        const firstHalfOverlapEnd = min([shiftMidpoint, viewEnd]);
        const firstHalfOverlapMinutes = Math.max(0, differenceInMinutes(firstHalfOverlapEnd, firstHalfOverlapStart));
        acceptedHours += firstHalfSlots * (firstHalfOverlapMinutes / 60);

        // Second half slots
        const secondHalfOverlapStart = max([shiftMidpoint, viewStart]);
        const secondHalfOverlapEnd = min([shiftEnd, viewEnd]);
        const secondHalfOverlapMinutes = Math.max(0, differenceInMinutes(secondHalfOverlapEnd, secondHalfOverlapStart));
        acceptedHours += secondHalfSlots * (secondHalfOverlapMinutes / 60);
      } else {
        // Fallback to slotsFilled
        acceptedHours += (shift.slotsFilled || 0) * overlapHours;
      }
    });

    const formatHoursAndMinutes = (totalHours: number) => {
      const roundedMinutes = Math.round(totalHours * 60);
      const hours = Math.floor(roundedMinutes / 60);
      const minutes = roundedMinutes % 60;
      const hourPart = hours > 0 ? `${hours} ${hours === 1 ? t("scheduler_hour") : t("scheduler_hours")}` : "";
      const minutePart = minutes > 0 ? `${minutes} ${minutes === 1 ? t("scheduler_minute") : t("scheduler_minutes")}` : "";
      if (hourPart && minutePart) return `${hourPart} ${t("scheduler_and")} ${minutePart}`;
      if (hourPart) return hourPart;
      if (minutePart) return minutePart;
      return t("scheduler_zero-hours");
    };

    return {
      totalPublishedHours: formatHoursAndMinutes(publishedHours),
      totalUnpublishedHours: formatHoursAndMinutes(unpublishedHours),
      totalAcceptedHours: formatHoursAndMinutes(acceptedHours),
    };
  }, [shifts, isWeekView, currentDate, weekStartDay, isLoading, t]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: `${SCHEDULER_INFO_BAR_HEIGHT}px`,
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 3,
        py: 0.5,
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #e0e0e0",
        width: "100%",
        height: `${SCHEDULER_INFO_BAR_HEIGHT}px`,
        boxSizing: "border-box",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
        {t("scheduler_accepted")}:{" "}
        <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
          {totalAcceptedHours}
        </Box>
      </Typography>
      <Typography variant="body2" sx={{ ml: 3, fontWeight: 500, color: "text.secondary" }}>
        {t("scheduler_published")}:{" "}
        <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
          {totalPublishedHours}
        </Box>
      </Typography>
      <Typography variant="body2" sx={{ ml: 3, fontWeight: 500, color: "text.secondary" }}>
        {t("scheduler_unpublished")}:{" "}
        <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
          {totalUnpublishedHours}
        </Box>
      </Typography>
    </Box>
  );
};

export default SchedulerInfoBar;
